import type { AssetMetricPoint } from '@src/providers/CoinMetricsApiProvider';
import { AggregationMethod } from './AggregatedMetric.types';

/** `[timestamp_ms, value]` data point matching Highcharts' line-series tuple shape. */
export type TimeSeriesPoint = [number, number];

/**
 * Groups a flat CoinMetrics response (asset rows interleaved) into one sorted
 * series per asset. Null values, non-finite parsed numbers, and unparseable
 * timestamps are dropped — the aggregation layer never has to think about them.
 */
export function groupByAsset(points: AssetMetricPoint[], metric: string): Map<string, TimeSeriesPoint[]> {
  const byAsset = new Map<string, TimeSeriesPoint[]>();

  for (const point of points) {
    const rawValue = point[metric];
    if (rawValue == null) {
      continue;
    }
    const value = Number(rawValue);
    if (!Number.isFinite(value)) {
      continue;
    }
    const timestamp = new Date(point.time).getTime();
    if (!Number.isFinite(timestamp)) {
      continue;
    }

    let series = byAsset.get(point.asset);
    if (series == null) {
      series = [];
      byAsset.set(point.asset, series);
    }
    series.push([timestamp, value]);
  }

  // Sort each per-asset series ascending so the aggregation pass below can rely on it.
  const sortedByAsset = new Map<string, TimeSeriesPoint[]>();
  for (const [cmAssetId, series] of byAsset) {
    sortedByAsset.set(
      cmAssetId,
      series.toSorted((a, b) => a[0] - b[0])
    );
  }
  return sortedByAsset;
}

/**
 * Aggregates the per-asset series across the union of all timestamps where at
 * least one selected asset has data. At each timestamp, an asset missing a
 * value is **skipped** — not treated as zero. For MEAN, the divisor is the
 * count of assets *present* at that timestamp.
 *
 * Why skip-not-zero: a new asset with sparse history shouldn't artificially
 * pull MEAN down toward zero during periods before its data starts. For SUM
 * the result is numerically identical either way.
 */
export function aggregate(
  dataByAsset: Map<string, TimeSeriesPoint[]>,
  selectedCmAssetIds: string[],
  method: AggregationMethod
): TimeSeriesPoint[] {
  if (selectedCmAssetIds.length === 0) {
    return [];
  }

  // Index every selected asset's series by timestamp once, so the union pass below is O(N).
  const valueByTimestampPerAsset: Map<number, number>[] = [];
  const allTimestamps = new Set<number>();

  for (const cmAssetId of selectedCmAssetIds) {
    const series = dataByAsset.get(cmAssetId);
    if (series == null) {
      continue;
    }
    const valueByTimestamp = new Map<number, number>();
    for (const [timestamp, value] of series) {
      valueByTimestamp.set(timestamp, value);
      allTimestamps.add(timestamp);
    }
    valueByTimestampPerAsset.push(valueByTimestamp);
  }

  const sortedTimestamps = Array.from(allTimestamps).toSorted((a, b) => a - b);
  const aggregated: TimeSeriesPoint[] = [];

  for (const timestamp of sortedTimestamps) {
    let sum = 0;
    let presentCount = 0;
    for (const valueByTimestamp of valueByTimestampPerAsset) {
      const value = valueByTimestamp.get(timestamp);
      if (value == null) {
        continue;
      }
      sum += value;
      presentCount += 1;
    }
    if (presentCount === 0) {
      continue;
    }
    const value = method === AggregationMethod.MEAN ? sum / presentCount : sum;
    aggregated.push([timestamp, value]);
  }
  return aggregated;
}
